import type { RefObject } from 'react';
import type CustomMapView from '@/views/map/CustomMapView';
import type { DeckGLRef } from '@deck.gl/react';
import { extractCoordinates } from '@/views/map/deckglUtils';

declare global {
  interface Window {
    __deckglTestHelpers?: {
      getLayerItemCoords: (
        layerId: string,
        objectType?: string,
        searchZone?: SearchZone
      ) => Promise<{
        x: number;
        y: number;
      } | null>;
      getLayerIds: () => string[];
      getLayerLoadedState: (layerId: string) => boolean | null;
      getSpanFillSamples: (searchZone?: SearchZone) => SpanFillSample[];
    };
  }
}

/**
 * Configuration for limiting the search area within the canvas
 */
export interface SearchZone {
  /** Distance from left edge in pixels (default: 300) */
  left?: number;
  /** Distance from top edge in pixels (default: 300) */
  top?: number;
  /** Width of search area in pixels (default: canvas width - left - right buffer) */
  width?: number;
  /** Height of search area in pixels (default: canvas height - top - bottom buffer) */
  height?: number;
}

/**
 * A rendered span's work status and the RGBA fill colour deck.gl computed for it.
 */
export interface SpanFillSample {
  workStatus: string | null;
  color: number[];
}

/**
 * Exposes deck.gl internals to window object for E2E testing.
 * This allows tests to get screen coordinates of layer features rendered in the canvas.
 */
export function exposeDeckGLTestHelpers(
  deckGlRef: RefObject<DeckGLRef<CustomMapView> | null>
): void {
  window.__deckglTestHelpers = {
    /**
     * Gets screen coordinates for the first item in a specific layer or sub-layer.
     * Uses deck.gl's pickObjects to find features and projects their world
     * coordinates to screen coordinates.
     */
    getLayerItemCoords: async (
      layerId: string,
      objectType?: string,
      searchZone?: SearchZone
    ): Promise<{ x: number; y: number } | null> => {
      const deck = deckGlRef.current?.deck;
      if (!deck) {
        return null;
      }

      // Get the canvas's client bounding box to calculate offsets
      const canvas = document.querySelector(
        '#deckgl-wrapper canvas'
      ) as HTMLCanvasElement;

      const canvasBounds = canvas.getBoundingClientRect();

      // Use provided search zone or default (safe area away from viewport edges)
      const EDGE_BUFFER = 300;
      const scanX = searchZone?.left ?? EDGE_BUFFER;
      const scanY = searchZone?.top ?? EDGE_BUFFER;
      const scanWidth = searchZone?.width ?? canvas.width - EDGE_BUFFER * 2;
      const scanHeight = searchZone?.height ?? canvas.height - EDGE_BUFFER * 2;
      const viewports = deck.getViewports?.();

      if (!viewports?.length) {
        return null;
      }

      // Get all objects in the specified layer within the scan area
      // Filter by objectType if provided
      const pickedObjects = deck
        .pickObjects({
          x: scanX,
          y: scanY,
          width: scanWidth,
          height: scanHeight,
        })
        ?.filter(
          (obj) =>
            (obj.sourceLayer?.id === layerId ||
              obj.layer?.id === layerId ||
              obj.layer?.id.startsWith(layerId)) &&
            (obj.object?.properties?.type === objectType || !objectType)
        );

      if (!pickedObjects?.length) {
        return null;
      }

      const { object: firstObject } = pickedObjects[0];

      // Extract coordinates from the object
      const coordinates = extractCoordinates(firstObject);
      if (!coordinates) {
        return null;
      }

      // Project the world coordinate [lng, lat] to screen [x, y]
      const screenCoords = viewports[0].project(coordinates);

      // Add canvas client offset to get viewport-relative coordinates for clicking
      const viewportX = screenCoords[0] + canvasBounds.left;
      const viewportY = screenCoords[1] + canvasBounds.top;

      // These are the coordinates to use for clicking!
      return {
        x: viewportX,
        y: viewportY,
      };
    },
    /**
     * Returns the ids of layers currently passed to DeckGL. A layer's presence
     * here means it has been *constructed* (and is therefore eligible to
     * request its tiles); a `visible: false` prop does not suppress that.
     *
     * deck.gl's LayersList allows nested arrays, so walk recursively and
     * collect anything that looks like a Layer (i.e. has a string id).
     */
    getLayerIds: (): string[] => {
      const ids: string[] = [];
      const walk = (item: unknown) => {
        if (Array.isArray(item)) {
          item.forEach(walk);
        } else if (item && typeof (item as { id?: unknown }).id === 'string') {
          ids.push((item as { id: string }).id);
        }
      };
      walk(deckGlRef.current?.deck?.props?.layers);
      return ids;
    },
    /**
     * Returns the `isLoaded` state of the layer with the given id, or null if
     * no such layer is currently constructed. For tiled layers deck.gl reports
     * isLoaded=true only once every visible tile has been fetched and decoded,
     * making this the deterministic "tiles are on the map" signal (the WebGL
     * canvas has no preserved drawing buffer, so pixel readback is not
     * reliable).
     */
    getLayerLoadedState: (layerId: string): boolean | null => {
      let loaded: boolean | null = null;
      const walk = (item: unknown) => {
        if (Array.isArray(item)) {
          item.forEach(walk);
          return;
        }
        const layer = item as { id?: unknown; isLoaded?: unknown } | null;
        if (layer?.id === layerId && typeof layer.isLoaded === 'boolean') {
          loaded = layer.isLoaded;
        }
      };
      walk(deckGlRef.current?.deck?.props?.layers);
      return loaded;
    },
    /**
     * Samples the span layer's rendered fill colours. For every picked span it
     * returns the feature's workStatus and the RGBA the span layer's own
     * getFillColor accessor produces, so tests can assert the work-status
     * overlay colouring (and that toggling it off recolours the same spans)
     * without relying on WebGL pixel readback.
     */
    getSpanFillSamples: (searchZone?: SearchZone): SpanFillSample[] => {
      const spanLayerId = 'scan';
      const deck = deckGlRef.current?.deck;
      const canvas = document.querySelector(
        '#deckgl-wrapper canvas'
      ) as HTMLCanvasElement | null;
      if (!deck || !canvas) {
        return [];
      }

      let getFillColor:
        | ((feature: unknown) => number[] | Uint8ClampedArray)
        | undefined;
      const findAccessor = (item: unknown) => {
        if (Array.isArray(item)) {
          item.forEach(findAccessor);
          return;
        }
        const layer = item as {
          id?: unknown;
          props?: { getFillColor?: unknown };
        } | null;
        if (
          layer?.id === spanLayerId &&
          typeof layer.props?.getFillColor === 'function'
        ) {
          getFillColor = layer.props.getFillColor as typeof getFillColor;
        }
      };
      findAccessor(deck.props?.layers);
      if (!getFillColor) {
        return [];
      }

      const EDGE_BUFFER = 100;
      const x = searchZone?.left ?? EDGE_BUFFER;
      const y = searchZone?.top ?? EDGE_BUFFER;
      const width = searchZone?.width ?? canvas.width - EDGE_BUFFER * 2;
      const height = searchZone?.height ?? canvas.height - EDGE_BUFFER * 2;

      const picked =
        deck
          .pickObjects({ x, y, width, height })
          ?.filter(
            (obj) =>
              obj.sourceLayer?.id === spanLayerId ||
              obj.layer?.id === spanLayerId ||
              obj.layer?.id?.startsWith(spanLayerId)
          ) ?? [];

      return picked
        .filter((obj) => obj.object?.properties)
        .map((obj) => ({
          workStatus: obj.object.properties.workStatus ?? null,
          color: Array.from(getFillColor!(obj.object)),
        }));
    },
  };
}

/**
 * Removes deck.gl test helpers from window object.
 */
export function removeDeckGLTestHelpers(): void {
  if (window.__deckglTestHelpers) {
    delete window.__deckglTestHelpers;
  }
}
